// Anita Week 2 / Day 5 — Week Project: Dancing Confetti (physics experiment).
// Project day: base 5 / max 8 nims. Validator requires parentGuide.rubric and
// reading-review-words / oral-defense-reading task ids on Day 5.
// Physics: static electricity (comb/balloon + paper bits + hair/wool).
// Math: 5×9 confetti, sum + compare three trials, observation table, fractions
// 1/2 and 1/3 of 36. Russian RU 1–2.

const day5 = {
  id: 'day-5',
  studentId: 'anita',
  weekId: 'week-2',
  dayId: 'day-5',
  status: 'active',
  title: 'Проект недели: Пляшущие конфетти / Week Project: Dancing Confetti',
  shortTitle: 'Пляшущие конфетти / Dancing Confetti',
  theme:
    'Маленький домашний физический опыт. Лёгкие бумажки «танцуют» — это статическое электричество. / A small home physics experiment. Tiny paper bits "dance" — that is static electricity.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'project', 'physics-experiment', 'spelling', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни приносит расчёску, шарик и кусочки бумаги. Сегодня Анита делает физический опыт «Пляшущие конфетти» и заполняет таблицу наблюдений. / Nimny brings a comb, a balloon and tiny paper bits. Today Anita runs the "Dancing Confetti" experiment and fills in an observation table.',
  goals: [
    'Провести опыт с расчёской/шариком и кусочками бумаги. / Run the comb/balloon + paper bits experiment.',
    'Записать наблюдения по 4 полям. / Record observations across 4 fields.',
    'Считать конфетти с умножением и вычитанием. / Count confetti with × and −.',
    'Считать сумму и сравнивать три опыта. / Sum three trials and compare.',
    'Читать таблицу наблюдений и делать вывод. / Read the observation table and conclude.',
    'Использовать дроби 1/2 и 1/3 от 36. / Use 1/2 and 1/3 of 36.'
  ],
  vocabulary: [
    { term: 'шарик / balloon', definition: 'воздушный шар. / a balloon.' },
    { term: 'бумага / paper', definition: 'материал для конфетти. / material for confetti.' },
    { term: 'двигаться / to move', definition: 'менять положение. / to change position.' },
    { term: 'лёгкий / light', definition: 'который весит мало. / weighing very little.' },
    { term: 'заряд / charge', definition: 'свойство, которое появляется при трении. / a property that appears from rubbing.' },
    { term: 'опыт / experiment', definition: 'попытка проверить, что произойдёт. / a try to see what happens.' }
  ],
  onlineTasks: [
    {
      id: 'reading-review-words', type: 'text',
      title: 'Слова недели / Words of the Week',
      prompt: 'Прочитай: время, периметр, таблица, среднее, сдача, шарик, бумага, заряд, опыт.',
      ruPrimaryText: 'Прочитай по-русски слова недели и слова опыта.',
      enHelpText: 'Read in Russian: time, perimeter, table, average, change, balloon, paper, charge, experiment.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения 9 русских слов.'
    },
    {
      id: 'oral-defense-reading', type: 'text',
      title: 'Ключевые фразы — защита проекта / Key Phrases — Project Defense',
      prompt:
        'Прочитай и попробуй сказать:\n1. Я сделала опыт «Пляшущие конфетти».\n2. Я потёрла шарик о волосы 10 секунд.\n3. Бумажки начали двигаться, хотя шарик их не касался.\n4. Это статическое электричество.\n5. Мой самый сильный опыт поднял ___ кусочков.',
      ruPrimaryText: 'Прочитай 5 ключевых фраз для устной защиты проекта.',
      enHelpText:
        '1. I ran the Dancing Confetti experiment.\n2. I rubbed the balloon on hair for 10 seconds.\n3. The paper bits began to move without the balloon touching them.\n4. This is static electricity.\n5. My strongest trial lifted ___ pieces.',
      answer: 'Попытка чтения и устной речи 5 фраз по-русски.'
    },
    {
      id: 'experiment-dancing-confetti',
      type: 'text',
      title: 'Опыт «Пляшущие конфетти» / Experiment: Dancing Confetti',
      prompt:
        'Материалы:\n— пластиковая расчёска или воздушный шарик;\n— маленькие кусочки бумаги;\n— волосы или шерстяной шарф.\n\nШаги:\n1. Нарежь маленькие кусочки бумаги.\n2. Положи их на стол.\n3. Потри расчёску или шарик о волосы или шерстяной шарф 10 секунд.\n4. Поднеси расчёску или шарик к бумажкам, но не касайся.\n5. Наблюдай, что происходит.\n\nЗапиши:\n— Что я сделала: ______\n— Что я увидела: ______\n— Почему, как я думаю: ______\n— Мой вывод: ______',
      ruPrimaryText: 'Сделай опыт с расчёской/шариком и бумажками. Запиши 4 наблюдения.',
      enHelpText: 'Materials: a plastic comb or a balloon; small paper bits; hair or a wool scarf. Run the comb/balloon experiment. Write down 4 observations: what I did, what I saw, why I think, my conclusion.',
      hint: 'Не подноси мелкие бумажки к лицу. После опыта убери бумагу со стола. / Do not bring paper bits close to your face. Clean up after the experiment.',
      answer: 'Open-ended наблюдение. Принимается, если есть все 4 поля и описано движение бумажек.'
    },
    {
      id: 'math-1-confetti-count',
      type: 'math',
      title: 'Math 1 — Конфетти / Confetti Count',
      prompt:
        'Анита нарезала 5 маленьких кучек бумаги.\nВ каждой кучке 9 кусочков.\nПотом 12 кусочков прилипли к шарику.\nСколько кусочков осталось на столе?',
      ruPrimaryText: '5 × 9 = ?. ? − 12 = ?',
      enHelpText: '5 piles × 9 bits each. Then 12 bits stick to the balloon. How many remain on the table?',
      helper: 'Сначала всего кусочков, потом минус 12. / Total first, then subtract 12.',
      answer: '33',
      unit: 'кусочков / bits'
    },
    {
      id: 'math-2-compare-trials',
      type: 'math',
      title: 'Math 2 — Сравни опыты / Compare Trials',
      prompt:
        'В первом опыте поднялось 14 кусочков бумаги.\nВо втором — 22.\nВ третьем — 18.\nСколько кусочков поднялось всего?\nНа сколько второй опыт был сильнее первого?\n\nЗапиши ответ через /: всего / разница. Например: 54 / 8.',
      ruPrimaryText: '14 + 22 + 18 = ?. 22 − 14 = ?',
      enHelpText: 'Trial 1: 14, Trial 2: 22, Trial 3: 18. Total? How much stronger trial 2 vs trial 1? Format as total / difference.',
      helper: 'Сначала сумма всех, потом сравнение 22 и 14. / Sum first, then compare 22 and 14.',
      answer: '54 / 8'
    },
    {
      id: 'math-3-observation-table',
      type: 'math',
      title: 'Math 3 — Таблица наблюдений / Observation Table',
      prompt:
        'Таблица:\nшарик — 24 кусочка\nрасчёска — 18 кусочков\nлинейка — 9 кусочков\n\nЧто подняло больше всего?\nНа сколько шарик поднял больше, чем линейка?\n\nЗапиши ответ через /: что / на сколько. Например: шарик / 15.',
      ruPrimaryText: 'Кто поднял больше? Шарик − линейка = ?',
      enHelpText: 'Balloon 24, comb 18, ruler 9. Which lifted most? Balloon − ruler = ? Format as what / by how many.',
      helper: 'Сравни и вычти. / Compare and subtract.',
      answer: 'шарик / 15'
    },
    {
      id: 'math-4-half-and-third-confetti',
      type: 'math',
      title: 'Math 4 — Половина и треть конфетти / Half and a Third of Confetti',
      prompt:
        'Было 36 кусочков бумаги.\nПоловина поднялась к шарику.\n1/3 всех кусочков осталась совсем неподвижной.\nСколько кусочков двигалось, но не поднялось?',
      ruPrimaryText: '1/2 от 36 + 1/3 от 36. Сколько двигалось, но не поднялось?',
      enHelpText: '36 bits total. 1/2 went up to the balloon. 1/3 stayed still. How many moved but did not lift?',
      helper: '1/2 от 36 = 18. 1/3 от 36 = 12. Что осталось? / 1/2 = 18, 1/3 = 12. Find the rest.',
      answer: '6',
      unit: 'кусочков / bits'
    },
    {
      id: 'logic-static-electricity',
      type: 'text',
      title: 'Логика — почему бумага двигается / Logic — Why the Paper Moves',
      prompt:
        'Ответь по-русски 2–3 короткие фразы:\n1. Почему бумага двигается, хотя шарик её не касается?\n2. Что изменится, если шарик потереть дольше?\n3. Что изменится, если бумажки будут крупнее?',
      ruPrimaryText: 'Три вопроса про опыт. Ответь короткими фразами.',
      enHelpText: '1. Why does the paper move without touching? 2. What changes if you rub longer? 3. What changes if pieces are bigger? Answer in 2–3 Russian sentences.',
      placeholder: 'Бумага двигается, потому что ...',
      answer:
        'Принимаются ответы вроде «появляется заряд», «дольше тереть = сильнее притяжение», «крупные бумажки тяжелее, поднимутся хуже».'
    },
    {
      id: 'spelling-review',
      type: 'text',
      title: 'Правописание недели / Weekly Spelling',
      prompt:
        'Напиши 5 любых слов из списка:\nвремя, периметр, таблица, среднее, сдача, шарик, бумага, заряд, опыт.',
      ruPrimaryText: 'Напиши 5 любых слов из списка недели.',
      enHelpText: 'Write any 5 words from the weekly list above.',
      answer: 'Любые 5 слов с попыткой правильного написания.'
    },
    {
      id: 'project-dancing-confetti',
      type: 'text',
      title: 'Проект «Пляшущие конфетти» / Project: Dancing Confetti',
      prompt:
        'Собери проект:\n1. Название опыта.\n2. Материалы (3 пункта).\n3. Шаги (5 пунктов).\n4. Таблица наблюдений (минимум 2 строки: шарик/расчёска).\n5. Вывод 1 предложением.\n6. 3 фразы устной защиты по-русски.',
      ruPrimaryText: 'Собери проект «Пляшущие конфетти» из 6 секций.',
      enHelpText: 'Build the Dancing Confetti project with all 6 sections.',
      answer: 'Open-ended project. Должны быть все 6 секций и устная защита родителю.'
    },
    {
      id: 'speech-week-summary',
      type: 'text',
      title: 'Расскажи родителю / Tell Your Parent',
      prompt:
        'Скажи по-русски: «Мой опыт показал …. Я думаю, это статическое электричество, потому что ….»',
      ruPrimaryText: 'Скажи по-русски 2 фразы про опыт и его объяснение.',
      enHelpText: 'Say in Russian: "My experiment showed ___. I think this is static electricity because ___."',
      answer: 'Принимаются 2 русские фразы с описанием результата и простой причиной.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши 9 слов недели.',
    '2. Проведи опыт «Пляшущие конфетти» и заполни 4 поля наблюдений.',
    '3. Реши Math 1–4 с шагами.',
    '4. Заполни таблицу наблюдений.',
    '5. Напиши 5 слов недели.',
    '6. Подготовь и скажи родителю 3 фразы защиты опыта.'
  ],
  printWorksheet: {
    title: 'Пляшущие конфетти / Dancing Confetti',
    subtitle: 'Неделя 2 · День 5 — проект и физический опыт / Week 2 · Day 5 — Project + Physics Experiment',
    pages: [
      {
        title: 'Слова и фразы недели / Weekly Words and Phrases',
        blocks: [
          { type: 'text', text: 'Название проекта: «Пляшущие конфетти». / Project name: "Dancing Confetti".' },
          { type: 'list', items: [
            'Слова недели: время, периметр, таблица, среднее, сдача, шарик, бумага, заряд, опыт. / Weekly words.',
            'Чтение 5 ключевых фраз для устной защиты (см. экран). / Key phrases.',
            'English help мелким шрифтом. / English help.'
          ] }
        ]
      },
      {
        title: 'Физический опыт и математика / Physics Experiment and Math',
        blocks: [
          { type: 'text', text: 'Материалы: пластиковая расчёска или воздушный шарик; маленькие кусочки бумаги; волосы или шерстяной шарф. / Materials.' },
          { type: 'list', items: [
            'Шаг 1. Нарежь маленькие кусочки бумаги. / Cut small paper bits.',
            'Шаг 2. Положи их на стол. / Place them on a table.',
            'Шаг 3. Потри расчёску или шарик о волосы/шерсть 10 секунд. / Rub for 10 seconds.',
            'Шаг 4. Поднеси расчёску или шарик к бумажкам, но не касайся. / Bring near without touching.',
            'Шаг 5. Наблюдай, что происходит. / Observe.',
            'Безопасность: не подноси бумажки к лицу. После опыта убери бумагу со стола. / Safety.',
            'Math 1. 5 × 9 = ___; ___ − 12 = ___ кусочков на столе. / Confetti count.',
            'Math 2. 14 + 22 + 18 = ___ всего; 22 − 14 = ___ разница. / Compare trials.',
            'Math 3. Таблица: шарик 24, расчёска 18, линейка 9. Лучший — ___; шарик − линейка = ___. / Observation table.',
            'Math 4. 1/2 от 36 = ___; 1/3 от 36 = ___; двигалось, но не поднялось = 36 − ___ − ___ = ___. / Half and a third.',
            'Логика: почему бумага двигается без касания? Что изменится при долгом трении и крупных бумажках? / Logic.'
          ] }
        ]
      },
      {
        title: 'Наблюдения, проект и parent check / Observations, Project and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Таблица наблюдений (минимум 2 строки): предмет — кусочков. / Observation table (≥ 2 rows).',
            'Что я сделала: __________________________ . / What I did.',
            'Что я увидела: __________________________ . / What I saw.',
            'Почему, как я думаю: __________________________ . / Why I think so.',
            'Мой вывод: __________________________ . / My conclusion.',
            'Правописание: напиши 5 любых слов недели. / Spelling: 5 words.',
            'Скажи родителю: «Мой опыт показал …. Я думаю, это статическое электричество, потому что …». / Oral defense.',
            'Parent rubric (см. parentGuide.rubric). / Parent rubric.',
            'Safety: бумажки убрать со стола после опыта. / Safety: clean up after.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита проводит первый домашний физический опыт, заполняет таблицу наблюдений, считает и объясняет результат.',
    mathGoal: 'U.S. Grade 3 review: × для групп (5 × 9), сумма из трёх и сравнение, таблица данных + вычитание, простые дроби 1/2 и 1/3 от 36.',
    russianGoal: 'Прочитать 9 слов недели и 5 ключевых фраз защиты.',
    readingWritingGoal: 'Прочитать фразы, написать 5 слов, заполнить 4 поля наблюдений.',
    practicalGoal: 'Провести опыт и объяснить его простыми словами.',
    whatThisLessonTeaches:
      'Анита получает первый осознанный опыт физики: видит движение без касания и связывает это с понятием «заряд».',
    keyMessage: 'Лёгкие бумажки притягиваются к заряженному предмету. Это статическое электричество.',
    howToExplain:
      'Натёртая о волосы или шерсть пластиковая расчёска или шарик получает статический заряд. Лёгкие предметы (маленькие бумажки) притягиваются к заряженной поверхности — поэтому они «прыгают» к расчёске или шарику.',
    exampleDialogue:
      'Parent: Что произошло, когда ты поднесла шарик?\nChild: Бумажки прыгали.\nParent: А почему, как ты думаешь?\nChild: Шарик стал заряжённым после трения.',
    readingSupport: 'Слова «заряд» и «опыт» — по слогам.',
    russianSpeechGoal: 'Анита говорит: «Мой опыт показал, что бумажки двигались. Это статическое электричество, потому что я натёрла шарик.»',
    mathAnswerKey:
      'Math 1: 5 × 9 = 45; 45 − 12 = 33 кусочка.\nMath 2: 14 + 22 + 18 = 54; 22 − 14 = 8.\nMath 3: лучший — шарик (24); шарик − линейка = 24 − 9 = 15.\nMath 4: 1/2 от 36 = 18; 1/3 от 36 = 12; 36 − 18 − 12 = 6.',
    spellingAnswerKey:
      'Принимаются любые 5 слов из списка:\nвремя\nпериметр\nтаблица\nсреднее\nсдача\nшарик\nбумага\nзаряд\nопыт',
    experimentAnswerKey:
      'Answer: бумажки начинают двигаться к расчёске или шарику без касания. Иногда они «прилипают» и держатся.\nSolution: при трении пластика о волосы или шерсть на пластике появляется статический заряд. Лёгкие бумажки притягиваются к заряженному предмету.\nParent note: Если опыт «не сработал» — попробовать ещё раз, потереть дольше или сильнее (10–15 секунд), сухой бумагой, в сухой день.',
    answerKey: [
      { taskId: 'reading-review-words', answer: 'Answer: попытка чтения 9 русских слов недели.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'oral-defense-reading', answer: 'Answer: попытка чтения 5 фраз защиты.\nSolution: Каждая фраза — отдельно.\nParent note: Главное — мысль, не идеальная фонетика.' },
      { taskId: 'experiment-dancing-confetti', answer: 'Answer: бумажки начинают двигаться к расчёске/шарику без касания.\nSolution: статический заряд после трения притягивает лёгкие бумажки.\nParent note: Безопасность — не подносить мелкие бумажки к лицу. После опыта убрать бумагу со стола.' },
      { taskId: 'math-1-confetti-count', answer: 'Answer: 33 кусочка.\nSolution: 5 × 9 = 45; 45 − 12 = 33.\nParent note: × + −.' },
      { taskId: 'math-2-compare-trials', answer: 'Answer: 54 / 8.\nSolution: 14 + 22 + 18 = 54; 22 − 14 = 8.\nParent note: Сумма и сравнение данных.' },
      { taskId: 'math-3-observation-table', answer: 'Answer: шарик / 15.\nSolution: лучший — шарик (24); 24 − 9 = 15.\nParent note: Чтение таблицы + вычитание.' },
      { taskId: 'math-4-half-and-third-confetti', answer: 'Answer: 6 кусочков.\nSolution: 1/2 от 36 = 18; 1/3 от 36 = 12; 36 − 18 − 12 = 6.\nParent note: Простые дроби от числа.' },
      { taskId: 'logic-static-electricity', answer: 'Answer: бумага двигается из-за статического заряда.\nSolution: Дольше трение → больше заряд → сильнее движение. Крупные бумажки тяжелее → их труднее поднять.\nParent note: Принимаются любые ответы с этими идеями.' },
      { taskId: 'spelling-review', answer: 'Answer: любые 5 слов из списка.\nSolution: Принимаются любые написания, если слово узнаётся.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'project-dancing-confetti', answer: 'Answer: проект из 6 секций.\nSolution: Название + материалы + 5 шагов + таблица + вывод + 3 фразы защиты.\nParent note: Если одна секция слабая — отметьте в рубрике, но не наказывайте.' },
      { taskId: 'speech-week-summary', answer: 'Answer: 2 русские фразы про опыт и причину.\nSolution: «Мой опыт показал … Я думаю, это статическое электричество, потому что …».\nParent note: Главное — мысль и простая причина.' }
    ],
    check: [
      'Анита прочитала 9 слов недели и 5 ключевых фраз защиты.',
      'Анита провела опыт и заполнила 4 поля наблюдений.',
      'Анита решила Math 1–4 с шагами.',
      'Анита заполнила таблицу наблюдений.',
      'Анита провела устную защиту 2+ фразами.'
    ],
    spellingCheck: 'Проверить 5 выбранных слов. Исправлять мягко.',
    readingCheck: 'Главное — попытка русского чтения и понимание смысла.',
    practicalCheck: 'Опыт проведён, наблюдения записаны, есть простой вывод.',
    drawingCriteria: 'Таблица наблюдений имеет минимум 2 строки и понятные числа.',
    safetyNotes:
      'Не подносить мелкие бумажки к лицу — могут попасть в глаз. Опыт делать только под присмотром взрослого. Воздушный шарик надувает родитель. После опыта убрать бумагу со стола; шарик — сдуть и убрать вне досягаемости маленьких детей и животных.',
    projectCriteria: [
      'Название опыта',
      'Список материалов (3 пункта)',
      'Список шагов (5 пунктов)',
      'Таблица наблюдений (≥ 2 строки)',
      'Вывод 1 предложением',
      '3 фразы устной защиты по-русски'
    ],
    rubric: [
      'Безопасность опыта (взрослый рядом, чистый стол, бумажки не у лица) — 15 баллов / Experiment safety — 15 points',
      'Качество наблюдений (4 поля заполнены) — 15 баллов / Observation quality — 15 points',
      'Русское чтение и устная защита — 15 баллов / Russian reading and oral defense — 15 points',
      'Математика Math 1–4 с шагами — 25 баллов / Math correctness with steps — 25 points',
      'Таблица наблюдений (≥ 2 строки) — 10 баллов / Observation table — 10 points',
      'Простое объяснение причины — 10 баллов / Simple cause explanation — 10 points',
      'Аккуратность и порядок на столе — 10 баллов / Neatness and cleanup — 10 points'
    ],
    commonMistakes: [
      '«Бумага двигается, потому что есть ветер.» Правильно: из-за статического заряда.',
      '«Шарик нужно потереть 1 секунду.» Лучше тереть 10–15 секунд.',
      '«Бумажки можно подносить к лицу.» Нет — они могут попасть в глаз.'
    ],
    discussionPrompts: [
      'Что было самым неожиданным?',
      'Что изменилось, когда ты потёрла дольше?',
      'Что было бы, если бумажки были мокрыми?'
    ],
    ifEasy: 'Попробуйте опыт с двумя разными предметами (шарик и расчёска) и посчитайте средний результат за 3 попытки.',
    ifDifficult: 'Если опыт «не сработал»: воздух сухой? попробовать в другой комнате; материал шерстяной?; тереть дольше (15 секунд).',
    rewardGuidance: 'Проектный день: базовая награда 5 нимов. Бонусы за безопасный опыт, хорошую таблицу наблюдений и устную защиту.',
    parentTheoryCard: {
      title: 'Статическое электричество (Day 5) / Static Electricity',
      purpose: 'Дать ребёнку первый осязаемый опыт физики и научить наблюдать.',
      keyMessage: 'При трении на пластике появляется заряд. Лёгкие предметы притягиваются.',
      parentScript:
        'Сначала покажите шарик. Объясните: «когда я тру его о волосы, он становится «липким» для лёгких бумажек, хотя не касается их». Запишите наблюдения вместе.',
      practiceSteps: [
        'Натрите шарик/расчёску 10 секунд.',
        'Поднесите к бумажкам без касания.',
        'Понаблюдайте 20 секунд.',
        'Запишите все 4 поля наблюдений и заполните таблицу.'
      ],
      doNotSay: [
        'Не говорите «это магия».',
        'Не пугайте словами «удар током».',
        'Не давайте ребёнку самостоятельно надувать шарик.'
      ],
      paperOnlyNotes:
        'Опыт безопасен на бумажном уровне. Никаких розеток, проводов или больших шаров с гелием. Все шарики — обычные воздушные, надутые взрослым.'
    },
    parentOnlyNotes: 'Если ребёнок боится — попробуйте сначала только с расчёской. После опыта — обязательно убрать бумагу.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 5, max: 8,
    label: 'Проектная награда / Project Reward', display: '5 нимов',
    bonuses: [
      { id: 'safe-experiment', label: 'Бонус за безопасный опыт', amount: 1 },
      { id: 'good-observation-table', label: 'Бонус за хорошую таблицу наблюдений', amount: 1 },
      { id: 'good-russian-defense', label: 'Бонус за устную защиту по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-review-words', 'oral-defense-reading',
      'experiment-dancing-confetti',
      'math-1-confetti-count', 'math-2-compare-trials',
      'math-3-observation-table', 'math-4-half-and-third-confetti',
      'logic-static-electricity',
      'spelling-review', 'project-dancing-confetti', 'speech-week-summary'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-3-day-1'
};

export default day5;
